import { GameEngine } from './engine/GameEngine';
import { UIManager } from './ui/UIManager';
import { GameConfig } from './types/GameTypes';
import { DEBUG_CONFIG } from './config/debug';

export class BopGame {
  private engine: GameEngine;
  private ui: UIManager;
  private playerColors: string[] = [
    '#ff6b6b', '#feca57', '#4ecdc4', '#9b59b6', 
    '#45b7d1', '#96ceb4', '#ff9ff3', '#54a0ff'
  ];

  constructor(config?: Partial<GameConfig>) {
    this.engine = new GameEngine(config);
    this.ui = new UIManager();
    
    this.setupEventListeners();
    this.ui.checkiOS();
    this.setupAutoPlayers();
    
    if (DEBUG_CONFIG.enabled) {
      this.ui.showDebugToast('Debug mode enabled! Press Ctrl+Shift+D to toggle panel');
      if (DEBUG_CONFIG.skipLobby && DEBUG_CONFIG.singlePlayerMode) {
        // Auto-start for debugging
        setTimeout(() => this.startGame(), 1000);
      }
    }
  }

  private setupAutoPlayers(): void {
    // Only add one human player in debug mode for single player testing
    const playersToAdd = DEBUG_CONFIG.enabled && DEBUG_CONFIG.singlePlayerMode ? 1 : 4;
    for (let i = 0; i < playersToAdd; i++) {
      this.addPlayer();
    }
  }

  private setupEventListeners(): void {
    this.ui.setupEventListeners(
      (position) => this.handleTouch(position),
      () => this.updateGameArea()
    );
  }

  private handleTouch(position: { x: number; y: number }): void {
    if (this.engine.gameState !== 'playing') return;
    
    this.ui.vibrate(30);
    const player = this.engine.jumpNearestPlayer(position);
    
    if (player) {
      // Visual feedback handled by Player class
    }
  }

  addPlayer(): void {
    try {
      const playerName = `Hráč ${this.engine.players.length + 1}`;
      const playerColor = this.playerColors[this.engine.players.length];
      const startPosition = { x: 100 + (this.engine.players.length * 70), y: 300 };
      
      this.engine.addPlayer(playerName, playerColor, startPosition);
      this.ui.updatePlayerList(this.engine.players);
    } catch (error) {
      this.ui.showToast('Maximum 8 hráčů!');
    }
  }

  startGame(): void {
    try {
      this.ui.hideLobby();
      this.ui.hideGameOver();
      
      const dimensions = this.ui.getGameAreaDimensions();
      this.ui.clearGameArea();
      this.ui.positionPlayersInArea(this.engine.players);
      
      this.engine.players.forEach(player => {
        this.ui.spawnPlayerElement(player);
      });
      
      this.engine.startGame(dimensions.width, dimensions.height);
      this.startGameLoop();
      
      this.ui.showToast('🎮 Ťukni na barevné bopy pro skok!');
    } catch (error) {
      this.ui.showToast('Potřebuješ alespoň 2 hráče!');
    }
  }

  private startGameLoop(): void {
    const gameLoop = setInterval(() => {
      if (this.engine.gameState !== 'playing') {
        clearInterval(gameLoop);
        return;
      }

      const dimensions = this.ui.getGameAreaDimensions();
      this.engine.updatePhysics(dimensions.width, dimensions.height);
      
      if (this.engine.needsPowerupSpawn()) {
        const powerup = this.engine.spawnPowerup(dimensions.width, dimensions.height);
        this.ui.spawnPowerupElement(powerup);
        this.engine.resetPowerupTimer();
      }
      
      this.ui.updateGameStats(
        this.engine.gameTime,
        this.engine.getAlivePlayers().length,
        this.engine.score
      );
      
      // Update debug info
      if (DEBUG_CONFIG.enabled && DEBUG_CONFIG.showDebugInfo) {
        this.ui.updateDebugInfo(this.engine.getDebugInfo());
      }
      
      if (this.engine.gameTime <= 0 || this.engine.getAlivePlayers().length <= 1) {
        this.endGame();
      }
    }, 100);
  }

  private endGame(): void {
    const winnerName = this.engine.endGame();
    this.ui.showGameOver(winnerName);
    this.ui.vibrate([200, 100, 200]);
  }

  pauseGame(): void {
    const dimensions = this.ui.getGameAreaDimensions();
    const wasPlaying = this.engine.gameState === 'playing';
    this.engine.pauseGame(dimensions.width, dimensions.height);
    
    if (wasPlaying) {
      this.ui.showToast('⏸️ Hra pozastavena');
    } else {
      this.ui.showToast('▶️ Hra pokračuje');
    }
  }

  restartGame(): void {
    const dimensions = this.ui.getGameAreaDimensions();
    this.engine.restartGame(dimensions.width, dimensions.height);
    this.startGame();
  }

  showLobby(): void {
    this.engine.returnToLobby();
    this.ui.showLobby();
    this.ui.hideGameOver();
  }

  private updateGameArea(): void {
    if (this.engine.gameState === 'playing') {
      this.ui.positionPlayersInArea(this.engine.players);
    }
  }

  toggleFullscreen(): void {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    if (isIOS) {
      window.scrollTo(0, 1);
      this.ui.showToast('📱 iOS: Posuvník nahoru skryje Safari lištu');
      return;
    }
    
    const elem = document.documentElement;
    const isFullscreen = document.fullscreenElement;
    
    if (!isFullscreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if ((elem as any).webkitRequestFullscreen) {
        (elem as any).webkitRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
    }
  }

  shareHighlight(): void {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const winnerText = document.getElementById('winner')?.textContent || '';
    
    if (isMobile && navigator.share) {
      navigator.share({
        title: '🎮 Bop Bop Battle!',
        text: `${winnerText} Zahraj si také! 🔥`,
        url: window.location.href
      }).catch(() => {
        this.ui.showToast('📤 Sdílení se nepodařilo');
      });
    } else if (navigator.clipboard) {
      const shareText = `🎮 Bop Bop Battle! ${winnerText} Zahraj si: ${window.location.href}`;
      navigator.clipboard.writeText(shareText).then(() => {
        this.ui.showToast('📋 Zkopírováno do schránky!');
      });
    } else {
      this.ui.showToast('📱 Highlight připraven k sdílení!');
    }
  }
}